import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../../../components/ui/button';
import { CdaLogo } from '../../../components/cda/CdaBrand';
import { LanguageBar } from '../../../i18n/cda';
import { CheckCircle, ArrowRight, FolderOpen, FileText, BookOpen, Camera } from 'lucide-react';

const CdaPortfolioHelp = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#fbf8f3]">
      <Helmet>
        <title>CDA Portfolio Help | Build Your Professional CDA Portfolio</title>
        <meta name="description" content="Get expert help building your CDA professional portfolio. Care Comply provides templates, checklists, and guidance for all six competency statements required for your Child Development Associate credential." />
        <link rel="canonical" href="https://carecomplycda.com/cda-portfolio-help" />
      </Helmet>

      <LanguageBar />
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/cda"><CdaLogo size={34} /></Link>
          <Button onClick={() => navigate('/cda')} className="bg-[#0d7d6f] hover:bg-[#0a6358]">
            Start Your Journey
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-12">
        <div className="text-center mb-12">
          <p className="text-[#c4622d] font-semibold mb-2">Portfolio Building</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            CDA Portfolio Help & Guidance
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Your CDA Professional Portfolio demonstrates your knowledge and skills. Care Comply helps you build a portfolio that impresses reviewers and showcases your best work.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">What is the CDA Professional Portfolio?</h2>
          <p className="text-slate-600 mb-4">
            The CDA Professional Portfolio is a collection of materials you gather throughout your CDA journey. It demonstrates your competence in working with young children and includes written statements, resource materials, and documentation of your professional growth.
          </p>
          <p className="text-slate-600 mb-4">
            You'll present this portfolio to a CDA Professional Development Specialist during your verification visit. A well-organized portfolio makes a strong impression and helps ensure a successful review.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-8 mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">The 6 CDA Competency Statements</h2>
          <p className="text-slate-600 mb-6">Your portfolio must include Competency Statements for each of these six areas:</p>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-[#fbf8f3] rounded-lg">
              <div className="w-8 h-8 rounded-full bg-[#0d7d6f] text-white font-bold flex items-center justify-center shrink-0">1</div>
              <div>
                <h3 className="font-semibold text-slate-900">Safe, Healthy Learning Environment</h3>
                <p className="text-sm text-slate-600">How you maintain a safe and healthy environment for children.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-[#fbf8f3] rounded-lg">
              <div className="w-8 h-8 rounded-full bg-[#0d7d6f] text-white font-bold flex items-center justify-center shrink-0">2</div>
              <div>
                <h3 className="font-semibold text-slate-900">Physical and Intellectual Development</h3>
                <p className="text-sm text-slate-600">Activities that advance physical and cognitive development.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-[#fbf8f3] rounded-lg">
              <div className="w-8 h-8 rounded-full bg-[#0d7d6f] text-white font-bold flex items-center justify-center shrink-0">3</div>
              <div>
                <h3 className="font-semibold text-slate-900">Social and Emotional Development</h3>
                <p className="text-sm text-slate-600">Supporting positive social and emotional growth.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-[#fbf8f3] rounded-lg">
              <div className="w-8 h-8 rounded-full bg-[#0d7d6f] text-white font-bold flex items-center justify-center shrink-0">4</div>
              <div>
                <h3 className="font-semibold text-slate-900">Positive Relationships with Families</h3>
                <p className="text-sm text-slate-600">Building productive relationships with children's families.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-[#fbf8f3] rounded-lg">
              <div className="w-8 h-8 rounded-full bg-[#0d7d6f] text-white font-bold flex items-center justify-center shrink-0">5</div>
              <div>
                <h3 className="font-semibold text-slate-900">Program Management</h3>
                <p className="text-sm text-slate-600">Managing an effective program operation.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-4 bg-[#fbf8f3] rounded-lg">
              <div className="w-8 h-8 rounded-full bg-[#0d7d6f] text-white font-bold flex items-center justify-center shrink-0">6</div>
              <div>
                <h3 className="font-semibold text-slate-900">Professionalism</h3>
                <p className="text-sm text-slate-600">Maintaining a commitment to professionalism.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <FolderOpen className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Binder Organization</h3>
            <p className="text-slate-600">Printable section dividers and a step-by-step guide to organizing your 3-ring binder.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <FileText className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Writing Templates</h3>
            <p className="text-slate-600">Sample competency statements and templates to help you write your own.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <BookOpen className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Resource Collection</h3>
            <p className="text-slate-600">Guidance on the 17 resource collection items required in your portfolio.</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-6">
            <Camera className="w-10 h-10 text-[#0d7d6f] mb-3" />
            <h3 className="text-lg font-bold text-slate-900 mb-2">Observation Prep</h3>
            <p className="text-slate-600">Prepare for the verification visit with checklists and tips.</p>
          </div>
        </div>

        <div className="bg-[#0d7d6f] rounded-2xl p-8 text-white text-center mb-8">
          <h2 className="text-2xl font-bold mb-3">Build Your Portfolio with Confidence</h2>
          <p className="mb-6 opacity-90">Care Comply guides you through every section of your CDA Professional Portfolio.</p>
          <Button onClick={() => navigate('/cda')} className="bg-white text-[#0d7d6f] hover:bg-slate-100">
            Get Portfolio Help <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        <div className="text-center text-sm text-slate-500">
          <p>Care Comply serves San Antonio, Austin, Houston, Dallas, and all of Texas.</p>
          <p className="mt-2">
            <Link to="/cda-career-coaching-san-antonio" className="text-[#0d7d6f] hover:underline">Career Coaching</Link> • 
            <Link to="/cda-480-hours-support" className="text-[#0d7d6f] hover:underline ml-2">480-Hour Support</Link> • 
            <Link to="/cda-job-readiness" className="text-[#0d7d6f] hover:underline ml-2">Job Readiness</Link>
          </p>
        </div>
      </main>
    </div>
  );
};

export default CdaPortfolioHelp;
